import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import { closeConnectionModal, fetchAllPublicKeys, deletePublicKey } from '../../integrationsSlice'
import { fetchAllSecrets } from '../../../../features/aws/awsSecretsManagerSlice'
import { baseUrl } from '../../../../modules/urls'
import { getAxiosDefaultConfig } from '../../../../modules/utils'
import { parseIntegrationTags, isShippoIntegrationKeyId } from '../../integrationTags'

const shippoConnectUrl = `${baseUrl}/auth/shippo/connect`
const shippoDisconnectUrl = `${baseUrl}/auth/shippo/disconnect`

interface FormValues {
  apiToken: string
  store: string
}

interface Channel {
  id: string
  ppKeys?: Array<{ keyId: string; keyValue: string; tags?: any }>
}

interface Props {
  ownerId: string | number
  retrieveAccounts: () => Promise<void>
  channel?: Channel
  accounts?: any[]
  allPublicKeys?: Array<{ keyId: string; keyValue?: string; tags?: any }>
}

type ViewMode = 'manage' | 'connect' | 'update'

interface ShippoConnRow {
  keyId: string
  merchant_id: string
  payhelm_channel: string
  token_preview: string
  isLegacy: boolean
}

function collectShippoRows(channel: Channel | undefined, allPublicKeys: any[] | undefined): ShippoConnRow[] {
  const map = new Map<string, any>()
  ;(allPublicKeys || []).forEach((row) => {
    if (!row?.keyId || !isShippoIntegrationKeyId(row.keyId)) return
    map.set(row.keyId, row)
  })
  ;(channel?.ppKeys || []).forEach((row: any) => {
    if (row?.keyId && isShippoIntegrationKeyId(row.keyId) && !map.has(row.keyId)) {
      map.set(row.keyId, row)
    }
  })
  return Array.from(map.values()).map((row) => {
    const tm = parseIntegrationTags(row.tags)
    const keyId = row.keyId as string
    const parts = keyId.split('--')
    const isLegacy = parts.length === 2 && parts[1] === 'shippo'
    return {
      keyId,
      merchant_id: tm.merchant_id || '',
      payhelm_channel: tm.payhelm_channel || '',
      token_preview: tm.shippo_token_preview || '',
      isLegacy,
    }
  })
}

const STORE_SEP = '\u001e'

export default function ShippoConnectionModalContent({
  ownerId,
  retrieveAccounts,
  channel,
  accounts = [],
  allPublicKeys,
}: Props) {
  const dispatch = useDispatch<any>()
  const closeModal = () => dispatch((closeConnectionModal as any)())

  const rows = useMemo(() => collectShippoRows(channel, allPublicKeys), [channel, allPublicKeys])
  const hasConnections = rows.length > 0

  const connectedStoreKeys = useMemo(() => {
    const set = new Set<string>()
    rows.forEach((r) => {
      if (r.isLegacy || !r.merchant_id || !r.payhelm_channel) return
      set.add(`${r.merchant_id}${STORE_SEP}${r.payhelm_channel}`)
    })
    return set
  }, [rows])

  const storeOptions = useMemo(() => {
    if (!Array.isArray(accounts)) return []
    const opts: { key: string; label: string; merchant_id: string; channel: string }[] = []
    accounts.forEach((a: any) => {
      const merchant_id = String(a?.merchant_id ?? '').trim()
      const ch = String(a?.channel ?? '').trim()
      if (!merchant_id || !ch) return
      const key = `${merchant_id}${STORE_SEP}${ch}`
      opts.push({
        key,
        label: `${ch} — ${merchant_id}`,
        merchant_id,
        channel: ch,
      })
    })
    return opts
  }, [accounts])

  const [viewMode, setViewMode] = useState<ViewMode>(hasConnections ? 'manage' : 'connect')
  const [showToken, setShowToken] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [isDisconnectingKey, setIsDisconnectingKey] = useState<string | null>(null)
  const [disconnectError, setDisconnectError] = useState<string | null>(null)
  const [editingRow, setEditingRow] = useState<ShippoConnRow | null>(null)

  useEffect(() => {
    setViewMode((cur) => {
      if (hasConnections && cur === 'connect' && !editingRow) return 'manage'
      if (!hasConnections && cur === 'manage') return 'connect'
      return cur
    })
  }, [hasConnections, editingRow])

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>()

  useEffect(() => {
    if (viewMode === 'update' && editingRow && !editingRow.isLegacy) {
      setValue(
        'store',
        `${editingRow.merchant_id}${STORE_SEP}${editingRow.payhelm_channel}`
      )
    }
  }, [viewMode, editingRow, setValue])

  const onSubmit = async ({ apiToken, store }: FormValues) => {
    setConnectError(null)
    const isUpdate = viewMode === 'update'
    let merchant_id: string
    let channelName: string
    if (isUpdate && editingRow && !editingRow.isLegacy) {
      merchant_id = editingRow.merchant_id
      channelName = editingRow.payhelm_channel
    } else {
      const opt = storeOptions.find((o) => o.key === store)
      if (!opt) {
        setConnectError('Please select a linked store.')
        return
      }
      merchant_id = opt.merchant_id
      channelName = opt.channel
    }

    try {
      await axios.post(
        shippoConnectUrl,
        {
          apiToken: apiToken.trim(),
          merchant_id,
          channel: channelName,
          ...(isUpdate ? { update: true } : {}),
        },
        getAxiosDefaultConfig()
      )
      await retrieveAccounts()
      if (ownerId) {
        dispatch((fetchAllPublicKeys as any)({ ownerId }))
      }
      dispatch((fetchAllSecrets as any)({ ownerId }))
      reset()
      setEditingRow(null)
      setViewMode('manage')
      dispatch((closeConnectionModal as any)())
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        'Failed to connect Shippo. Please check your token and try again.'
      setConnectError(msg)
    }
  }

  const disconnectRow = async (row: ShippoConnRow) => {
    if (!window.confirm('Disconnect Shippo for this store?')) return
    setDisconnectError(null)
    setIsDisconnectingKey(row.keyId)
    try {
      const payload =
        row.isLegacy || (!row.merchant_id && !row.payhelm_channel)
          ? {}
          : { merchant_id: row.merchant_id, channel: row.payhelm_channel }
      await axios.delete(shippoDisconnectUrl, {
        ...getAxiosDefaultConfig(),
        data: payload,
      })
      dispatch((deletePublicKey as any)({ keyId: row.keyId }))
      if (ownerId) {
        dispatch((fetchAllPublicKeys as any)({ ownerId }))
        dispatch((fetchAllSecrets as any)({ ownerId }))
      }
      await retrieveAccounts()
      if (rows.length <= 1) {
        dispatch((closeConnectionModal as any)())
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        'Failed to disconnect Shippo. Please try again.'
      setDisconnectError(msg)
    } finally {
      setIsDisconnectingKey(null)
    }
  }

  const instructionsBlock = (
    <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4 tw-mb-5 tw-text-sm tw-text-blue-900">
      <p className="tw-mb-2 tw-font-semibold">How to find your Shippo API token:</p>
      <ol className="tw-list-decimal tw-pl-4 tw-space-y-1">
        <li>
          Log in to your Shippo account and go to{' '}
          <a
            href="https://goshippo.com/user/apikeys/"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-blue-600 tw-underline hover:tw-text-blue-800"
          >
            goshippo.com/user/apikeys
            <FontAwesomeIcon icon={faExternalLinkAlt} className="tw-ml-1 tw-text-xs" />
          </a>
        </li>
        <li>
          Copy your <strong>Live Token</strong> (starts with <code>shippo_live_…</code>) for
          production, or your <strong>Test Token</strong> (<code>shippo_test_…</code>) for testing.
        </li>
      </ol>
      <p className="tw-mt-2 tw-text-xs tw-text-blue-700">
        Choose which PayHelm store this Shippo account labels shipments for. You can connect multiple stores with the same or different tokens.
      </p>
    </div>
  )

  const availableStores = storeOptions.filter((o) => !connectedStoreKeys.has(o.key))

  const tokenForm = (
    <form onSubmit={handleSubmit(onSubmit)}>
      {viewMode !== 'update' && (
        <div className="tw-mb-4">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            PayHelm store
          </label>
          <select
            {...register('store', {
              required: viewMode === 'connect' ? 'Select a linked store' : false,
            })}
            disabled={availableStores.length === 0}
            className="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
          >
            <option value="">
              {availableStores.length === 0 ? 'No remaining stores to link' : 'Select store…'}
            </option>
            {availableStores.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
          {errors.store && (
            <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.store.message}</p>
          )}
        </div>
      )}
      {viewMode === 'update' && editingRow && (
        <div className="tw-mb-4 tw-text-sm tw-text-gray-700">
          <strong>Store:</strong> {editingRow.payhelm_channel} — {editingRow.merchant_id}
        </div>
      )}
      <div className="tw-mb-4">
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
          {viewMode === 'update' ? 'New Shippo API Token' : 'Shippo API Token'}
        </label>
        <div className="tw-flex tw-items-center tw-gap-2">
          <input
            {...register('apiToken', {
              required: 'API token is required',
              validate: (v) => v.trim().length > 0 || 'API token cannot be empty',
            })}
            type={showToken ? 'text' : 'password'}
            placeholder="shippo_live_… or shippo_test_…"
            className="tw-flex-1 tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
          />
          <button
            type="button"
            className="tw-text-gray-500 hover:tw-text-gray-800 tw-text-lg"
            onClick={() => setShowToken((s) => !s)}
            aria-label={showToken ? 'Hide token' : 'Show token'}
          >
            {showToken ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>
        {errors.apiToken && (
          <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.apiToken.message}</p>
        )}
      </div>

      {connectError && (
        <div className="tw-bg-red-50 tw-border tw-border-red-300 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-red-700 tw-mb-4">
          {connectError}
        </div>
      )}

      <div className="tw-flex tw-items-center tw-justify-end tw-space-x-2">
        <Button
          variant="secondary"
          onClick={() => {
            setConnectError(null)
            reset()
            setEditingRow(null)
            if (viewMode === 'update') {
              setViewMode('manage')
            } else if (hasConnections) {
              setViewMode('manage')
            } else {
              closeModal()
            }
          }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="tw-mr-2" />
              {viewMode === 'update' ? 'Updating…' : 'Connecting…'}
            </>
          ) : viewMode === 'update' ? (
            'Save token'
          ) : (
            'Connect'
          )}
        </Button>
      </div>
    </form>
  )

  if (viewMode === 'manage' && hasConnections) {
    return (
      <div className="tw-p-6">
        <h2 className="tw-text-xl tw-mb-4">Manage Shippo</h2>

        <div className="tw-space-y-3 tw-mb-6">
          {rows.map((row) => {
            const storeLabel = row.isLegacy
              ? 'Legacy (no store) — reconnect with a store selected'
              : `${row.payhelm_channel} — ${row.merchant_id}`
            return (
              <div
                key={row.keyId}
                className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-p-4 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg"
              >
                <div className="tw-min-w-0 tw-flex-1">
                  <p className="tw-font-medium tw-text-gray-900">{storeLabel}</p>
                  <p className="tw-text-sm tw-text-gray-600">
                    Token: <span className="tw-font-mono">{row.token_preview || '—'}</span>
                  </p>
                </div>
                <div className="tw-flex tw-shrink-0 tw-gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={!!isDisconnectingKey || row.isLegacy}
                    onClick={() => {
                      if (row.isLegacy) return
                      setConnectError(null)
                      reset()
                      setEditingRow(row)
                      setViewMode('update')
                    }}
                  >
                    Update token
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={!!isDisconnectingKey}
                    onClick={() => disconnectRow(row)}
                  >
                    {isDisconnectingKey === row.keyId ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin className="tw-mr-1" />
                        …
                      </>
                    ) : (
                      'Disconnect'
                    )}
                  </Button>
                </div>
              </div>
            )
          })}
        </div>

        {disconnectError && (
          <div className="tw-bg-red-50 tw-border tw-border-red-300 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-red-700 tw-mb-4">
            {disconnectError}
          </div>
        )}

        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <Button
            variant="primary"
            disabled={availableStores.length === 0}
            onClick={() => {
              setConnectError(null)
              reset()
              setEditingRow(null)
              setViewMode('connect')
            }}
          >
            Connect another store
          </Button>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </div>
        {availableStores.length === 0 && (
          <p className="tw-text-xs tw-text-gray-500 tw-mt-2">
            All linked PayHelm stores already have a Shippo connection.
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="tw-p-6">
      <h2 className="tw-text-xl tw-mb-2">
        {viewMode === 'update' ? 'Update Shippo token' : 'Connect Shippo'}
      </h2>
      {instructionsBlock}
      {tokenForm}
    </div>
  )
}
